import Phaser from 'phaser';
import {
  BUTTON_HEIGHT,
  BUTTON_STROKE_WIDTH_DEFAULT,
  BUTTON_STROKE_WIDTH_PRIMARY,
  BUTTON_WIDTH,
  COLORS,
  FONT_HEADER,
} from '../style.js';

export type ButtonStyle = 'default' | 'primary';

export interface ButtonOptions {
  readonly x: number;
  readonly y: number;
  readonly width?: number;
  readonly height?: number;
  readonly text: string;
  readonly style?: ButtonStyle;
  readonly onTap: () => void;
}

export class Button {
  private readonly bg: Phaser.GameObjects.Rectangle;
  private readonly label: Phaser.GameObjects.Text;
  private readonly width: number;
  private readonly height: number;
  private enabled = true;
  private style: ButtonStyle;
  private onTap: () => void;

  constructor(scene: Phaser.Scene, opts: ButtonOptions) {
    this.width = opts.width ?? BUTTON_WIDTH;
    this.height = opts.height ?? BUTTON_HEIGHT;
    this.style = opts.style ?? 'default';
    this.onTap = opts.onTap;

    this.bg = scene.add.rectangle(
      opts.x,
      opts.y,
      this.width,
      this.height,
      this.normalFill()
    );
    this.bg.setStrokeStyle(this.strokeWidth(), this.strokeColor());
    this.bg.setInteractive({ cursor: 'pointer' });

    this.label = scene.add
      .text(opts.x, opts.y, opts.text.toUpperCase(), {
        fontFamily: FONT_HEADER,
        fontSize: '17px',
        color: this.labelColor(),
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    this.bg.on('pointerdown', () => {
      if (this.enabled) this.onTap();
    });
    this.bg.on('pointerover', () => {
      if (this.enabled) this.bg.setFillStyle(this.hoverFill());
    });
    this.bg.on('pointerout', () => {
      if (this.enabled) this.bg.setFillStyle(this.normalFill());
    });
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.bg.setFillStyle(
      enabled ? this.normalFill() : COLORS.buttonBgDisabledInt
    );
    this.bg.setStrokeStyle(
      this.strokeWidth(),
      enabled ? this.strokeColor() : COLORS.borderSubtleInt
    );
    this.label.setColor(enabled ? this.labelColor() : COLORS.textMuted);
    this.label.setAlpha(enabled ? 1 : 0.55);
  }

  setText(text: string): void {
    this.label.setText(text.toUpperCase());
  }

  setOnTap(fn: () => void): void {
    this.onTap = fn;
  }

  setVisible(visible: boolean): void {
    this.bg.setVisible(visible);
    this.label.setVisible(visible);
    if (visible) this.bg.setInteractive({ cursor: 'pointer' });
    else this.bg.disableInteractive();
  }

  /**
   * Lift the button above (or below) other display objects. Used by
   * modal-style overlays so the button sits on top of a backdrop.
   */
  setDepth(depth: number): void {
    this.bg.setDepth(depth);
    this.label.setDepth(depth);
  }

  /**
   * Tear down the underlying display objects. Used by transient hosts
   * (e.g. the game-over modal) when they leave the screen.
   */
  destroy(): void {
    this.bg.destroy();
    this.label.destroy();
  }

  private normalFill(): number {
    return this.style === 'primary'
      ? COLORS.buttonBgPrimaryInt
      : COLORS.buttonBgInt;
  }

  private hoverFill(): number {
    return this.style === 'primary'
      ? COLORS.buttonBgPrimaryHoverInt
      : COLORS.buttonBgHoverInt;
  }

  private strokeColor(): number {
    return this.style === 'primary'
      ? COLORS.buttonStrokePrimaryInt
      : COLORS.buttonStrokeInt;
  }

  private strokeWidth(): number {
    return this.style === 'primary'
      ? BUTTON_STROKE_WIDTH_PRIMARY
      : BUTTON_STROKE_WIDTH_DEFAULT;
  }

  private labelColor(): string {
    return this.style === 'primary'
      ? COLORS.accentCyanHex
      : COLORS.textPrimary;
  }
}
